(() => {
  const ROOT_SELECTOR = '[data-goodlot-loox-reviews]';
  const REVIEW_JUMP_SELECTOR =
    '[data-goodlot-reviews-jump], a[href="#GoodLotCustomerReviews"]';
  const REVIEW_SCROLL_OFFSET = 88;
  const desktopScrollMedia = window.matchMedia('(min-width: 990px)');
  const mobileReviewNavMedia = window.matchMedia('(max-width: 749px)');
  const REVIEW_PRODUCT_NAV_KEY = 'goodlot-review-product-navigation';

  const resetMobileProductNavigationScroll = () => {
    if (!mobileReviewNavMedia.matches) return;

    try {
      const raw = window.sessionStorage.getItem(REVIEW_PRODUCT_NAV_KEY);
      if (!raw) return;

      const state = JSON.parse(raw);
      const isFresh =
        state &&
        state.pathname === window.location.pathname &&
        Date.now() - asNumber(state.timestamp, 0) < 15000;

      window.sessionStorage.removeItem(REVIEW_PRODUCT_NAV_KEY);

      if (!isFresh) return;

      const previousRestoration =
        'scrollRestoration' in window.history
          ? window.history.scrollRestoration
          : null;

      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }

      const forceTop = () => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'instant',
        });
      };

      /*
       * Mobile browsers/themes can restore the previous product-page scroll
       * position after the new product DOM paints. Re-assert top position for
       * a short window so navigation from a review's product pill always lands
       * at the beginning of the destination product page.
       */
      forceTop();
      window.requestAnimationFrame(forceTop);
      window.setTimeout(forceTop, 60);
      window.setTimeout(forceTop, 180);
      window.setTimeout(() => {
        forceTop();

        if (
          previousRestoration &&
          'scrollRestoration' in window.history
        ) {
          window.history.scrollRestoration = previousRestoration;
        }
      }, 420);
    } catch (error) {
      window.sessionStorage.removeItem(REVIEW_PRODUCT_NAV_KEY);
    }
  };

  const cleanupLooxReviewParam = () => {
    try {
      const url = new URL(window.location.href);

      if (url.searchParams.get('ref') !== 'review') return;

      /*
       * Loox uses ?ref=review to open the review form on page load.
       * Give Loox enough time to consume the parameter, then remove only
       * that parameter from the visible URL so a later refresh does not
       * reopen the form.
       *
       * This does not navigate, reload, scroll, or touch any other query
       * params such as variant/UTM values.
       */
      window.setTimeout(() => {
        const currentUrl = new URL(window.location.href);

        if (currentUrl.searchParams.get('ref') !== 'review') return;

        currentUrl.searchParams.delete('ref');

        const cleanUrl =
          currentUrl.pathname +
          (currentUrl.searchParams.toString()
            ? `?${currentUrl.searchParams.toString()}`
            : '') +
          currentUrl.hash;

        window.history.replaceState(window.history.state, '', cleanUrl);
      }, 2500);
    } catch (error) {
      console.warn(
        '[goodlot-loox-reviews] Could not clean Loox review URL:',
        error
      );
    }
  };

  resetMobileProductNavigationScroll();

  if (document.readyState === 'complete') {
    cleanupLooxReviewParam();
  } else {
    window.addEventListener('load', cleanupLooxReviewParam, { once: true });
    window.addEventListener('load', resetMobileProductNavigationScroll, { once: true });
  }

  const scrollToReviews = (event) => {
    const trigger = event.target.closest?.(REVIEW_JUMP_SELECTOR);
    if (!trigger) return;

    const target =
      document.getElementById('GoodLotCustomerReviews') ||
      document.querySelector('[data-goodlot-loox-reviews]');
    if (!target) return;

    event.preventDefault();

    /*
     * Match Horizon's real vertical scroll owner.
     *
     * Desktop Horizon scrolls .page-wrapper.
     * Smaller viewports scroll the document viewport.
     */
    if (desktopScrollMedia.matches) {
      const pageWrapper = document.querySelector('.page-wrapper');

      if (pageWrapper) {
        const wrapperRect = pageWrapper.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const targetTop = Math.max(
          0,
          pageWrapper.scrollTop +
            (targetRect.top - wrapperRect.top) -
            REVIEW_SCROLL_OFFSET
        );

        pageWrapper.scrollTo({
          top: targetTop,
          behavior: 'smooth',
        });

        return;
      }
    }

    // Preserve the mobile behavior that was already working correctly.
    target.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    });
  };

  document.addEventListener('click', scrollToReviews);

  const STAR_PATH =
    'm12 2.5 2.84 5.75 6.35.92-4.6 4.48 1.09 6.33L12 17l-5.68 2.98 1.09-6.33-4.6-4.48 6.35-.92L12 2.5Z';

  const escapeHtml = (value = '') =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const getFirst = (object, paths, fallback = '') => {
    for (const path of paths) {
      const value = path.split('.').reduce((current, key) => current?.[key], object);
      if (value !== undefined && value !== null && value !== '') return value;
    }
    return fallback;
  };

  const asNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const parseMedia = (review) => {
    const source =
      getFirst(review, ['media', 'images', 'photos', 'attachments', 'reviewMedia'], []) || [];

    const items = Array.isArray(source) ? source : [source];

    return items
      .map((item) => {
        if (typeof item === 'string') {
          return { url: item, thumbnail: item, type: 'image' };
        }

        const url = getFirst(item, ['url', 'src', 'originalUrl', 'original_url', 'imageUrl', 'image_url']);
        if (!url) return null;

        const thumbnail =
          getFirst(item, ['thumbnailUrl', 'thumbnail_url', 'previewUrl', 'preview_url', 'thumb', 'url'], url);

        const type = String(getFirst(item, ['type', 'mediaType', 'media_type'], 'image')).toLowerCase();

        return { url, thumbnail, type };
      })
      .filter(Boolean);
  };

  const normalizeReview = (review, index) => {
    const rating = Math.max(0, Math.min(5, asNumber(getFirst(review, ['rating', 'score', 'stars']), 0)));

    const reviewer = getFirst(
      review,
      ['reviewerName', 'reviewer_name', 'reviewer.name', 'author', 'customer.name', 'name'],
      'GoodLot customer'
    );

    const body = getFirst(review, ['content', 'body', 'review', 'text', 'message', 'comment'], '');
    const title = getFirst(review, ['title', 'headline', 'subject'], '');

    // Prefer the review's actual submitted/imported date.
    // For imported Loox reviews, created_at is the date supplied in the import file.
    // Camel-case createdAt is kept only as a fallback because it can represent
    // the API/import record creation timestamp instead of the intended review date.
    const date = getFirst(
      review,
      [
        'created_at',
        'reviewed_at',
        'reviewDate',
        'review_date',
        'submitted_at',
        'date',
        'createdAt',
        'submittedAt',
        'published_at',
        'publishedAt',
      ],
      ''
    );

    const verified = Boolean(
      getFirst(
        review,
        [
          'verifiedPurchase',
          'verified_purchase',
          'isVerifiedPurchase',
          'is_verified_purchase',
          'verifiedBuyer',
          'verified_buyer',
          'verified',
        ],
        false
      )
    );

    const productTitle = getFirst(
      review,
      ['productTitle', 'product_title', 'product.name', 'product.title', 'itemTitle', 'item_title'],
      ''
    );

    const productUrl = getFirst(
      review,
      ['productUrl', 'product_url', 'product.url', 'itemUrl', 'item_url'],
      ''
    );

    const productId = getFirst(
  review,
  [
    'productId',
    'product_id',
    'product.id',
    'itemId',
    'item_id',
  ],
  ''
);

const productHandle = getFirst(
  review,
  [
    'productHandle',
    'product_handle',
    'product.handle',
    'itemHandle',
    'item_handle',
  ],
  ''
);

    const variantTitle = getFirst(
      review,
      ['variantTitle', 'variant_title', 'variant.name', 'variant.title', 'itemType', 'item_type'],
      ''
    );

    const helpful = asNumber(
      getFirst(review, ['helpfulCount', 'helpful_count', 'helpful', 'votes.helpful'], 0),
      0
    );

    return {
      id: String(getFirst(review, ['id', 'reviewId', 'review_id', '_id'], `review-${index}`)),
      rating,
      reviewer: String(reviewer),
      title: String(title || ''),
      body: String(body || ''),
      date,
      verified,
      productTitle: String(productTitle || ''),
productUrl: String(productUrl || ''),
productId: String(productId || ''),
productHandle: String(productHandle || ''),
variantTitle: String(variantTitle || ''),
      media: parseMedia(review),
    };
  };

  const extractReviewArray = (payload) => {
    const candidates = [
      payload?.reviews,
      payload?.items,
      payload?.results,
      payload?.productReviews,
      payload?.product_reviews,
      payload?.data?.reviews,
      payload?.data?.items,
      payload?.data?.results,
      payload?.data?.productReviews,
      payload?.data?.product_reviews,
      Array.isArray(payload?.data) ? payload.data : null,
      Array.isArray(payload) ? payload : null,
    ];

    return candidates.find(Array.isArray) || [];
  };

  const extractNextPage = (payload, currentUrl) => {
    /*
     * Loox has returned pagination metadata in more than one shape over time.
     * Keep this parser deliberately tolerant so the storefront does not stop
     * after the first API batch while shop.metafields.loox.global_stats says
     * more published reviews exist.
     */

    const directUrl = getFirst(payload, [
      'next',
      'nextUrl',
      'next_url',
      'nextPageUrl',
      'next_page_url',
      'links.next',
      'links.next.href',
      'pagination.next',
      'pagination.nextUrl',
      'pagination.next_url',
      'pagination.nextPageUrl',
      'pagination.next_page_url',
      'pagination.links.next',
      'pagination.links.next.href',
      'meta.next',
      'meta.nextUrl',
      'meta.next_url',
      'meta.nextPageUrl',
      'meta.next_page_url',
      'data.next',
      'data.nextUrl',
      'data.next_url',
      'data.nextPageUrl',
      'data.next_page_url',
      'data.links.next',
      'data.links.next.href',
      'data.pagination.next',
      'data.pagination.nextUrl',
      'data.pagination.next_url',
      'data.pagination.nextPageUrl',
      'data.pagination.next_page_url',
      'data.pagination.links.next',
      'data.pagination.links.next.href',
      'data.meta.next',
      'data.meta.nextUrl',
      'data.meta.next_url',
      'data.meta.nextPageUrl',
      'data.meta.next_page_url',
    ]);

    if (typeof directUrl === 'string' && directUrl.trim()) {
      try {
        return new URL(directUrl, currentUrl).toString();
      } catch {
        // Continue through the other pagination strategies below.
      }
    }

    const cursor = getFirst(payload, [
      'nextCursor',
      'next_cursor',
      'endCursor',
      'end_cursor',
      'pageInfo.endCursor',
      'page_info.end_cursor',
      'pagination.nextCursor',
      'pagination.next_cursor',
      'pagination.endCursor',
      'pagination.end_cursor',
      'meta.nextCursor',
      'meta.next_cursor',
      'data.nextCursor',
      'data.next_cursor',
      'data.endCursor',
      'data.end_cursor',
      'data.pageInfo.endCursor',
      'data.page_info.end_cursor',
      'data.pagination.nextCursor',
      'data.pagination.next_cursor',
      'data.pagination.endCursor',
      'data.pagination.end_cursor',
      'data.meta.nextCursor',
      'data.meta.next_cursor',
    ]);

    const cursorHasNext = Boolean(
      getFirst(payload, [
        'hasNextPage',
        'has_next_page',
        'pageInfo.hasNextPage',
        'page_info.has_next_page',
        'pagination.hasNextPage',
        'pagination.has_next_page',
        'data.hasNextPage',
        'data.has_next_page',
        'data.pageInfo.hasNextPage',
        'data.page_info.has_next_page',
        'data.pagination.hasNextPage',
        'data.pagination.has_next_page',
      ], cursor ? true : false)
    );

    if (cursor && cursorHasNext) {
      const url = new URL(currentUrl);
      /*
       * Existing GoodLot/Loox integration used `cursor`; preserve that first.
       * If Loox returns GraphQL-style pageInfo/endCursor, the same cursor value
       * is still carried forward without exposing any private API key.
       */
      url.searchParams.set('cursor', String(cursor));
      return url.toString();
    }

    const nextPage = asNumber(
      getFirst(payload, [
        'nextPage',
        'next_page',
        'pagination.nextPage',
        'pagination.next_page',
        'meta.nextPage',
        'meta.next_page',
        'data.nextPage',
        'data.next_page',
        'data.pagination.nextPage',
        'data.pagination.next_page',
        'data.meta.nextPage',
        'data.meta.next_page',
      ]),
      0
    );

    if (nextPage > 0) {
      const url = new URL(currentUrl);
      url.searchParams.set('page', String(nextPage));
      return url.toString();
    }

    let page = asNumber(
      getFirst(payload, [
        'page',
        'currentPage',
        'current_page',
        'pagination.page',
        'pagination.currentPage',
        'pagination.current_page',
        'meta.page',
        'meta.currentPage',
        'meta.current_page',
        'data.page',
        'data.currentPage',
        'data.current_page',
        'data.pagination.page',
        'data.pagination.currentPage',
        'data.pagination.current_page',
        'data.meta.page',
        'data.meta.currentPage',
        'data.meta.current_page',
      ]),
      0
    );

    if (page <= 0) {
      try {
        page = asNumber(new URL(currentUrl).searchParams.get('page'), 1);
      } catch {
        page = 1;
      }
    }

    const totalPages = asNumber(
      getFirst(payload, [
        'totalPages',
        'total_pages',
        'lastPage',
        'last_page',
        'pageCount',
        'page_count',
        'pagination.totalPages',
        'pagination.total_pages',
        'pagination.lastPage',
        'pagination.last_page',
        'pagination.pageCount',
        'pagination.page_count',
        'meta.totalPages',
        'meta.total_pages',
        'meta.lastPage',
        'meta.last_page',
        'meta.pageCount',
        'meta.page_count',
        'data.totalPages',
        'data.total_pages',
        'data.lastPage',
        'data.last_page',
        'data.pageCount',
        'data.page_count',
        'data.pagination.totalPages',
        'data.pagination.total_pages',
        'data.pagination.lastPage',
        'data.pagination.last_page',
        'data.pagination.pageCount',
        'data.pagination.page_count',
        'data.meta.totalPages',
        'data.meta.total_pages',
        'data.meta.lastPage',
        'data.meta.last_page',
      ]),
      0
    );

    if (page > 0 && totalPages > page) {
      const url = new URL(currentUrl);
      url.searchParams.set('page', String(page + 1));
      return url.toString();
    }

    const limit = asNumber(
      getFirst(payload, [
        'limit',
        'pageSize',
        'page_size',
        'perPage',
        'per_page',
        'pagination.limit',
        'pagination.pageSize',
        'pagination.page_size',
        'pagination.perPage',
        'pagination.per_page',
        'meta.limit',
        'meta.pageSize',
        'meta.page_size',
        'meta.perPage',
        'meta.per_page',
        'data.limit',
        'data.pageSize',
        'data.page_size',
        'data.perPage',
        'data.per_page',
        'data.pagination.limit',
        'data.pagination.pageSize',
        'data.pagination.page_size',
        'data.pagination.perPage',
        'data.pagination.per_page',
        'data.meta.limit',
        'data.meta.pageSize',
        'data.meta.page_size',
        'data.meta.perPage',
        'data.meta.per_page',
      ]),
      0
    );

    const total = asNumber(
      getFirst(payload, [
        'total',
        'totalCount',
        'total_count',
        'count',
        'pagination.total',
        'pagination.totalCount',
        'pagination.total_count',
        'meta.total',
        'meta.totalCount',
        'meta.total_count',
        'data.total',
        'data.totalCount',
        'data.total_count',
        'data.pagination.total',
        'data.pagination.totalCount',
        'data.pagination.total_count',
        'data.meta.total',
        'data.meta.totalCount',
        'data.meta.total_count',
      ]),
      0
    );

    const hasMore = Boolean(
      getFirst(payload, [
        'hasMore',
        'has_more',
        'pagination.hasMore',
        'pagination.has_more',
        'meta.hasMore',
        'meta.has_more',
        'data.hasMore',
        'data.has_more',
        'data.pagination.hasMore',
        'data.pagination.has_more',
        'data.meta.hasMore',
        'data.meta.has_more',
      ], false)
    );

    if (
      page > 0 &&
      (hasMore || (limit > 0 && total > 0 && page * limit < total))
    ) {
      const url = new URL(currentUrl);
      url.searchParams.set('page', String(page + 1));
      return url.toString();
    }

    const offset = asNumber(
      getFirst(payload, [
        'offset',
        'pagination.offset',
        'meta.offset',
        'data.offset',
        'data.pagination.offset',
        'data.meta.offset',
      ]),
      Number.NaN
    );

    if (
      Number.isFinite(offset) &&
      limit > 0 &&
      total > 0 &&
      offset + limit < total
    ) {
      const url = new URL(currentUrl);
      url.searchParams.set('offset', String(offset + limit));
      url.searchParams.set('limit', String(limit));
      return url.toString();
    }

    return '';
  };

  const formatDate = (value) => {
    if (!value) return '';

    const raw = String(value).trim();

    // Parse date-only values as a local calendar date instead of UTC.
    // This prevents an imported value such as 2026-07-16 from rendering
    // as Jul 15 for shoppers in negative UTC offsets such as California.
    const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const date = dateOnlyMatch
      ? new Date(
          Number(dateOnlyMatch[1]),
          Number(dateOnlyMatch[2]) - 1,
          Number(dateOnlyMatch[3])
        )
      : new Date(raw);

    if (Number.isNaN(date.getTime())) return '';

    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const starSvg = (fill = 100) => {
    const safeFill = Math.max(0, Math.min(100, fill));
    const clip = `goodlot-star-${Math.random().toString(36).slice(2)}`;
    const clipWidth = (24 * safeFill) / 100;

    return `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <defs>
          <clipPath id="${clip}">
            <rect x="0" y="0" width="${clipWidth}" height="24"></rect>
          </clipPath>
        </defs>
        <path d="${STAR_PATH}" fill="#d6d8da"></path>
        <path d="${STAR_PATH}" fill="#0a0a0a" clip-path="url(#${clip})"></path>
      </svg>
    `;
  };

  const renderStars = (rating) => {
    let html = '';

    for (let index = 1; index <= 5; index += 1) {
      const fill = Math.max(0, Math.min(1, rating - (index - 1))) * 100;
      html += starSvg(fill);
    }

    return html;
  };

  const reviewText = (review) => `${review.title} ${review.body}`.toLowerCase();

  /*
   * Reviews with customer media (photo OR video) always rank before
   * text-only reviews. This is source-agnostic: imported reviews and reviews
   * written by customers are treated exactly the same.
   *
   * The decorated sort keeps the existing relative order stable inside each
   * group unless a secondary comparator (such as Newest) is supplied.
   */
  const hasReviewMedia = (review) =>
    Array.isArray(review?.media) && review.media.length > 0;

  const reviewDateValue = (review) => {
  if (!review?.date) return 0;

  const value = new Date(review.date).getTime();

  return Number.isFinite(value) ? value : 0;
};

const normalizeProductPath = (value) => {
  if (!value) return '';

  try {
    const url = new URL(value, window.location.origin);

    return url.pathname
      .replace(/\/+$/, '')
      .toLowerCase();
  } catch {
    return String(value)
      .split('?')[0]
      .split('#')[0]
      .replace(/\/+$/, '')
      .toLowerCase();
  }
};

const sortReviewsForProductPage = (
  reviews,
  isCurrentProductReview
) =>
  reviews
    .map((review, index) => ({
      review,
      index,
    }))
    .sort((a, b) => {

      /* 1. Current product reviews ALWAYS first */
      const currentPriority =
        Number(isCurrentProductReview(b.review)) -
        Number(isCurrentProductReview(a.review));

      if (currentPriority !== 0) {
        return currentPriority;
      }


      /* 2. Photo / video reviews first */
      const mediaPriority =
        Number(hasReviewMedia(b.review)) -
        Number(hasReviewMedia(a.review));

      if (mediaPriority !== 0) {
        return mediaPriority;
      }


      /* 3. Newest → oldest inside each group */
      const datePriority =
        reviewDateValue(b.review) -
        reviewDateValue(a.review);

      if (datePriority !== 0) {
        return datePriority;
      }


      /* 4. Stable fallback */
      return a.index - b.index;
    })
    .map(({ review }) => review);

  const MENTION_MAX = 6;

  const MENTION_RULES = [
    {
      label: 'Great value',
      terms: ['price', 'value', 'deal', 'worth', 'saving', 'save', 'discount', 'retail', 'full price'],
    },
    {
      label: 'Condition matched',
      terms: ['as described', 'condition', 'matched', 'open box', 'brand new', 'flawless', 'spotless', 'exactly as described'],
    },
    {
      label: 'Fast shipping',
      terms: ['shipping', 'delivery', 'delivered', 'arrived', 'fast', 'quick', 'same day'],
    },
    {
      label: 'Packaging',
      terms: ['packaging', 'package', 'box', 'boxed', 'carton', 'packed', 'sealed'],
    },
    {
      label: 'Helpful service',
      terms: ['customer service', 'service', 'support', 'helpful', 'response', 'responded'],
    },
    {
      label: 'Easy to use',
      terms: ['easy to use', 'simple to use', 'easy to handle', 'convenient', 'straightforward'],
    },
    {
      label: 'Good quality',
      terms: ['quality', 'solid', 'sturdy', 'premium', 'well made', 'well-made'],
    },
    {
      label: 'Strong performance',
      terms: ['performance', 'powerful', 'strong', 'works great', 'works perfectly', 'works really well'],
    },
    {
      label: 'Easy setup',
      terms: ['easy to set up', 'easy setup', 'setup was easy', 'set up', 'ready to use'],
    },
    {
      label: 'Lightweight',
      terms: ['lightweight', 'light weight', 'easy to move', 'easy to carry'],
    },
    {
      label: 'Battery life',
      terms: ['battery', 'battery life', 'charge', 'charging', 'runtime', 'run time'],
    },
    {
      label: 'Nice design',
      terms: ['design', 'looks great', 'looks good', 'beautiful', 'nice looking', 'sleek'],
    },
    {
      label: 'Cleaning results',
      terms: ['clean', 'cleaner', 'cleaning', 'suction', 'picks up', 'pet hair'],
    },
    {
      label: 'Quiet operation',
      terms: ['quiet', 'quieter', 'noise', 'not loud'],
    },
    {
      label: 'Would recommend',
      terms: ['recommend', 'recommended', 'would buy again', 'buy again', 'very happy', 'couldn\'t be happier'],
    },
  ];

  class GoodLotLooxReviews {
    constructor(root) {
      this.root = root;
      this.list = root.querySelector('[data-goodlot-review-list]');
      this.status = root.querySelector('[data-goodlot-review-status]');
      this.filters = root.querySelector('[data-goodlot-review-filters]');
      this.allReviewsButton = root.querySelector('[data-goodlot-review-all]');
      this.setupNote = root.querySelector('[data-goodlot-review-setup-note]');
      this.mentions = root.querySelector('[data-goodlot-review-mentions]');
      this.mentionsList = root.querySelector('[data-goodlot-review-mentions-list]');
      this.score = root.querySelector('[data-goodlot-review-score]');
      this.count = root.querySelector('[data-goodlot-review-count]');
      this.summaryStars = root.querySelector('[data-goodlot-summary-stars]');

      this.mediaModal = null;
      this.mediaModalImage = null;
      this.mediaModalClose = null;
      this.mediaModalTrigger = null;
      this.mediaModalKeydownHandler = (event) => {
        if (event.key === 'Escape' && this.mediaModal && !this.mediaModal.hidden) {
          this.closeMediaModal();
        }
      };

      this.pageSize = Math.max(3, asNumber(root.dataset.pageSize, 5));
      this.revealStep = 5;
      this.publicStoreId = root.dataset.publicStoreId?.trim() || '';
      this.currentProductId =
  String(root.dataset.currentProductId || '').trim();

this.currentProductHandle =
  String(root.dataset.currentProductHandle || '')
    .trim()
    .toLowerCase();

this.currentProductUrl =
  String(root.dataset.currentProductUrl || '').trim();
      this.endpoint = this.publicStoreId
        ? `https://storefront-api.loox.io/storefront/v1/store/${encodeURIComponent(
            this.publicStoreId
          )}/product-reviews`
        : '';

      this.allReviews = [];
      this.filteredReviews = [];
      this.visibleCount = this.pageSize;
      this.activeFilter = 'all';
      this.nextPageUrl = '';
      this.showingAllReviews = false;
      this.prefetchComplete = false;
      this.publicDataExhausted = false;
      this.totalReviewCount = Math.max(
        0,
        asNumber(root.dataset.liquidCount, 0)
      );
    }
    isCurrentProductReview(review) {
  if (!review) return false;


  /* Product ID — strongest match */
  if (
    this.currentProductId &&
    review.productId &&
    String(review.productId) === this.currentProductId
  ) {
    return true;
  }


  /* Shopify handle */
  if (
    this.currentProductHandle &&
    review.productHandle &&
    String(review.productHandle).toLowerCase() ===
      this.currentProductHandle
  ) {
    return true;
  }


  /* Product URL — reliable fallback */
  const currentPath =
    normalizeProductPath(this.currentProductUrl);

  const reviewPath =
    normalizeProductPath(review.productUrl);

  if (
    currentPath &&
    reviewPath &&
    currentPath === reviewPath
  ) {
    return true;
  }


  return false;
}
    async init() {
      if (!this.publicStoreId) {
        this.status.hidden = true;
        this.setupNote.hidden = false;
        return;
      }

      this.allReviewsButton?.addEventListener('click', () => this.handleAllReviews());

      this.root.addEventListener('click', (event) => {
        const productLink = event.target.closest?.(
          'a.goodlot-loox-review__product[href]'
        );

        if (
          productLink &&
          this.root.contains(productLink) &&
          mobileReviewNavMedia.matches
        ) {
          try {
            const targetUrl = new URL(productLink.href, window.location.href);

            /*
             * Loox may return http://goodlot.shop/... even though the live
             * storefront is HTTPS. Normalize same-host product navigation so
             * mobile browsers do one clean same-origin navigation.
             */
            if (targetUrl.hostname === window.location.hostname) {
              event.preventDefault();
              event.stopPropagation();

              const normalizePath = (pathname) =>
                pathname.replace(/\/+$/, '') || '/';

              const targetPath = normalizePath(targetUrl.pathname);
              const currentPath = normalizePath(window.location.pathname);

              /*
               * If this review belongs to the product page the shopper is
               * already viewing, do NOT reload/navigate to the same URL.
               *
               * Reloading the same mobile product URL can cause the browser or
               * theme to restore the previous review-section scroll position
               * after briefly showing the top of the page.
               *
               * Instead, keep the current document and move directly to the
               * product area at the top.
               */
              if (targetPath === currentPath) {
                window.sessionStorage.removeItem(REVIEW_PRODUCT_NAV_KEY);

                const cleanCurrentUrl =
                  window.location.pathname + window.location.search;

                if (window.location.hash) {
                  window.history.replaceState(
                    window.history.state,
                    '',
                    cleanCurrentUrl
                  );
                }

                const pageWrapper = document.querySelector('.page-wrapper');

                if (pageWrapper && pageWrapper.scrollHeight > pageWrapper.clientHeight) {
                  pageWrapper.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth',
                  });
                }

                window.scrollTo({
                  top: 0,
                  left: 0,
                  behavior: 'smooth',
                });

                return;
              }

              /*
               * Different product: perform one clean same-origin navigation and
               * mark the destination so mobile scroll restoration cannot send
               * the shopper back to the review section.
               */
              window.sessionStorage.setItem(
                REVIEW_PRODUCT_NAV_KEY,
                JSON.stringify({
                  pathname: targetUrl.pathname,
                  timestamp: Date.now(),
                })
              );

              const cleanTarget =
                window.location.origin +
                targetUrl.pathname +
                targetUrl.search;

              window.location.assign(cleanTarget);
              return;
            }
          } catch (error) {
            // Fall back to the anchor's normal navigation.
          }
        }

        const trigger = event.target.closest?.('[data-goodlot-review-photo-open]');
        if (!trigger) return;

        event.preventDefault();

        const imageUrl = trigger.dataset.goodlotReviewPhotoOpen || '';
        if (!imageUrl) return;

        this.openMediaModal(imageUrl, trigger);
      });

      await this.fetchPage(this.endpoint);
    }

    async requestReviews(url, attempt = 0) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 8000);

      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
          credentials: 'omit',
          cache: 'no-store',
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Loox Storefront API returned ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        if (attempt === 0) {
          return this.requestReviews(url, 1);
        }

        throw error;
      } finally {
        window.clearTimeout(timeout);
      }
    }

    async fetchPage(url) {
      try {
        this.status.hidden = false;
        this.allReviewsButton.hidden = true;

        const payload = await this.requestReviews(url);
        this.mergeReviewPayload(payload);
        this.nextPageUrl = extractNextPage(payload, url);

        /*
         * Important: filters and "Customers mention" describe the complete
         * public review dataset, not only the reviews currently visible.
         * Load every public Loox page into memory first, then render only the
         * first pageSize reviews. The More reviews button only reveals +5.
         */
        await this.prefetchAllPublicReviews();

        this.status.hidden = true;
        this.updateSummaryFromReviews();
        this.renderMentions();
        this.renderFilters();
        this.applyFilter(this.activeFilter);
      } catch (error) {
        console.warn('[goodlot-loox-reviews] Storefront API request failed:', error);
        this.status.hidden = true;
        this.allReviewsButton.hidden = true;
        this.list.innerHTML = `
          <p class="goodlot-loox-reviews__empty">
            Reviews are temporarily unavailable. Please refresh and try again.
          </p>
        `;
      }
    }

    mergeReviewPayload(payload) {
      const reviewArray = extractReviewArray(payload);

      const normalized = reviewArray
        .map((review, index) =>
          normalizeReview(review, this.allReviews.length + index)
        )
        .filter((review) => review.rating > 0 || review.body || review.title);

      const knownIds = new Set(this.allReviews.map((review) => review.id));
      let added = 0;

      normalized.forEach((review) => {
        if (!knownIds.has(review.id)) {
          this.allReviews.push(review);
          knownIds.add(review.id);
          added += 1;
        }
      });

      return added;
    }

    async fetchAndMerge(url) {
      const payload = await this.requestReviews(url);
      const added = this.mergeReviewPayload(payload);
      const next = extractNextPage(payload, url);
      return { added, next, payload };
    }

    buildApiUrl(params = {}) {
      const url = new URL(this.endpoint);

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          url.searchParams.set(key, String(value));
        }
      });

      return url.toString();
    }

    async prefetchAllPublicReviews() {
      const expected = this.totalReviewCount;
      const seenUrls = new Set();
      let safety = 0;

      /* First follow whatever pagination pointer Loox actually returned. */
      while (
        this.nextPageUrl &&
        safety < 50 &&
        (!expected || this.allReviews.length < expected)
      ) {
        const nextUrl = this.nextPageUrl;
        if (seenUrls.has(nextUrl)) break;
        seenUrls.add(nextUrl);

        this.nextPageUrl = '';
        const result = await this.fetchAndMerge(nextUrl);
        this.nextPageUrl = result.next || '';
        safety += 1;

        if (result.added === 0 && !this.nextPageUrl) break;
      }

      /*
       * Some Loox Storefront responses have been observed without a usable
       * next-page pointer. If Liquid says more reviews exist, probe several
       * common pagination forms. Every probe is duplicate-safe, so an ignored
       * parameter cannot duplicate cards.
       */
      if (expected > 0 && this.allReviews.length < expected) {
        const expandedCandidates = [
          this.buildApiUrl({ limit: Math.min(Math.max(expected, 100), 250) }),
          this.buildApiUrl({ per_page: Math.min(Math.max(expected, 100), 250) }),
          this.buildApiUrl({ page_size: Math.min(Math.max(expected, 100), 250) }),
        ];

        for (const candidate of expandedCandidates) {
          if (this.allReviews.length >= expected) break;
          if (seenUrls.has(candidate)) continue;
          seenUrls.add(candidate);

          try {
            const result = await this.fetchAndMerge(candidate);
            if (result.next) this.nextPageUrl = result.next;

            while (
              this.nextPageUrl &&
              safety < 50 &&
              this.allReviews.length < expected
            ) {
              const nextUrl = this.nextPageUrl;
              if (seenUrls.has(nextUrl)) {
                this.nextPageUrl = '';
                break;
              }
              seenUrls.add(nextUrl);

              this.nextPageUrl = '';
              const nextResult = await this.fetchAndMerge(nextUrl);
              this.nextPageUrl = nextResult.next || '';
              safety += 1;

              if (nextResult.added === 0 && !this.nextPageUrl) break;
            }
          } catch (error) {
            // Try the next harmless pagination shape.
          }
        }
      }

      /* Page-number fallback. Stops immediately if Loox ignores page=. */
      if (expected > 0 && this.allReviews.length < expected) {
        for (let page = 2; page <= 25 && this.allReviews.length < expected; page += 1) {
          const candidate = this.buildApiUrl({ page, limit: 100 });
          if (seenUrls.has(candidate)) continue;
          seenUrls.add(candidate);

          try {
            const result = await this.fetchAndMerge(candidate);
            if (result.added === 0) break;
          } catch (error) {
            break;
          }
        }
      }

      /* Offset fallback. Stops if the endpoint does not honor offset=. */
      if (expected > 0 && this.allReviews.length < expected) {
        let offset = this.allReviews.length;

        for (let attempt = 0; attempt < 10 && this.allReviews.length < expected; attempt += 1) {
          const candidate = this.buildApiUrl({ offset, limit: 100 });
          if (seenUrls.has(candidate)) break;
          seenUrls.add(candidate);

          try {
            const before = this.allReviews.length;
            const result = await this.fetchAndMerge(candidate);
            if (result.added === 0) break;

            offset = this.allReviews.length;
            if (this.allReviews.length <= before) break;
          } catch (error) {
            break;
          }
        }
      }

      this.prefetchComplete = true;
      this.publicDataExhausted = !this.nextPageUrl;

      if (expected > 0 && this.allReviews.length < expected) {
        console.warn(
          '[goodlot-loox-reviews] Loox public-data mismatch:',
          `${this.allReviews.length} public reviews loaded, but Liquid reports ${expected}.`,
          'The missing reviews are not being returned by the Storefront API. Check Loox Status = Published for the missing reviews.'
        );
      }
    }

    updateSummaryFromReviews() {
      const liquidRating = asNumber(this.root.dataset.liquidRating, 0);
      const liquidCount = asNumber(this.root.dataset.liquidCount, 0);

      const averageFromReviews =
        this.allReviews.length > 0
          ? this.allReviews.reduce((sum, review) => sum + review.rating, 0) / this.allReviews.length
          : 0;

      const rating = liquidRating || averageFromReviews;
      const count = liquidCount || this.allReviews.length;

      if (rating > 0) {
        this.score.textContent = rating.toFixed(1);
        this.summaryStars.innerHTML = renderStars(rating);
        this.summaryStars.setAttribute('aria-label', `${rating.toFixed(1)} out of 5 stars`);
      }

      if (count > 0) {
        this.count.textContent = `${count} review${count === 1 ? '' : 's'}`;
      }
    }

    renderMentions() {
      if (!this.mentions || !this.mentionsList || this.allReviews.length === 0) return;

      const scored = MENTION_RULES.map((rule) => {
        let hits = 0;

        this.allReviews.forEach((review) => {
          const text = reviewText(review);
          if (rule.terms.some((term) => text.includes(term))) hits += 1;
        });

        return { ...rule, hits };
      })
        .filter((rule) => rule.hits > 0)
        .sort((a, b) => b.hits - a.hits)
        .slice(0, MENTION_MAX);

      if (scored.length === 0) {
        this.mentions.hidden = true;
        return;
      }

      this.mentionsList.innerHTML = scored
        .map(
          (rule) => `
            <span class="goodlot-loox-reviews__mention">
              <span class="goodlot-loox-reviews__mention-check" aria-hidden="true">✓</span>
              ${escapeHtml(rule.label)}
            </span>
          `
        )
        .join('');

      this.mentions.hidden = false;
    }

    renderFilters() {
      if (!this.filters) return;

      const counts = {
        all: this.totalReviewCount > 0 ? this.totalReviewCount : this.allReviews.length,
        photos: this.allReviews.filter((review) => review.media.length > 0).length,
        five: this.allReviews.filter((review) => review.rating >= 4.75).length,
        four: this.allReviews.filter((review) => review.rating >= 3.75 && review.rating < 4.75).length,
        low: this.allReviews.filter((review) => review.rating <= 2.49).length,
      };

      const filters = [
        ['all', `All reviews (${counts.all})`],
        ['photos', `With photos (${counts.photos})`],
        ['five', `5 stars (${counts.five})`],
        ['four', `4 stars (${counts.four})`],
        ['low', `1–2 stars (${counts.low})`],
        ['newest', 'Newest'],
      ];

      this.filters.innerHTML = filters
        .map(
          ([key, label]) => `
            <button
              class="goodlot-loox-reviews__filter${key === this.activeFilter ? ' is-active' : ''}"
              type="button"
              data-goodlot-review-filter="${key}"
            >
              ${escapeHtml(label)}
            </button>
          `
        )
        .join('');

      this.filters.querySelectorAll('[data-goodlot-review-filter]').forEach((button) => {
        button.addEventListener('click', () => {
          this.activeFilter = button.dataset.goodlotReviewFilter || 'all';
          this.showingAllReviews = false;
          this.visibleCount = this.pageSize;

          this.filters
            .querySelectorAll('[data-goodlot-review-filter]')
            .forEach((item) => item.classList.toggle('is-active', item === button));

          this.applyFilter(this.activeFilter);
        });
      });

      this.filters.hidden = false;
    }

    applyFilter(filter) {
      let reviews = [...this.allReviews];

      if (filter === 'photos') {
        reviews = reviews.filter((review) => review.media.length > 0);
      } else if (filter === 'five') {
        reviews = reviews.filter((review) => review.rating >= 4.75);
      } else if (filter === 'four') {
        reviews = reviews.filter((review) => review.rating >= 3.75 && review.rating < 4.75);
      } else if (filter === 'low') {
        reviews = reviews.filter((review) => review.rating <= 2.49);
      }

      /*
       * Media-first is a global display rule:
       * - All reviews: photo/video reviews first.
       * - Rating filters: photo/video reviews first inside that rating group.
       * - With photos: unchanged because every result already has media.
       * - Newest: media reviews still stay first; each group is then sorted
       *   newest-to-oldest.
       */
      reviews = sortReviewsForProductPage(
  reviews,
  (review) => this.isCurrentProductReview(review)
);
      this.filteredReviews = reviews;
      this.renderReviews();
    }

    renderReviews() {
      if (!this.list) return;

      if (this.filteredReviews.length === 0) {
        this.list.innerHTML = `
          <p class="goodlot-loox-reviews__empty">No reviews match this filter yet.</p>
        `;
        this.allReviewsButton.hidden = true;
        return;
      }

      const visibleReviews = this.filteredReviews.slice(0, this.visibleCount);
      this.list.innerHTML = visibleReviews.map((review) => this.reviewTemplate(review)).join('');

      /*
       * All public data is prefetched before this renders. More reviews is now
       * a pure reveal control and can never stay visible because Liquid reports
       * a larger number than the Storefront API actually exposes.
       */
      const hasMoreLocal = this.visibleCount < this.filteredReviews.length;
      this.allReviewsButton.hidden = !hasMoreLocal;

      if (!this.allReviewsButton.hidden) {
        this.allReviewsButton.textContent = 'More reviews';
      }
    }

    ensureMediaModal() {
      if (this.mediaModal) return;

      const modal = document.createElement('div');
      modal.className = 'goodlot-loox-media-modal';
      modal.setAttribute('data-goodlot-review-media-modal', '');
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-label', 'Review photo');
      modal.hidden = true;

      modal.innerHTML = `
        <button
          class="goodlot-loox-media-modal__close"
          type="button"
          aria-label="Close review photo"
          data-goodlot-review-media-close
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18"></path>
          </svg>
        </button>

        <div class="goodlot-loox-media-modal__stage">
          <img
            class="goodlot-loox-media-modal__image"
            data-goodlot-review-media-image
            src=""
            alt="Customer review photo"
          >
        </div>
      `;

      document.body.appendChild(modal);

      this.mediaModal = modal;
      this.mediaModalImage = modal.querySelector('[data-goodlot-review-media-image]');
      this.mediaModalClose = modal.querySelector('[data-goodlot-review-media-close]');

      modal.addEventListener('click', (event) => {
        if (
          event.target === modal ||
          event.target.closest?.('[data-goodlot-review-media-close]')
        ) {
          this.closeMediaModal();
        }
      });
    }

    openMediaModal(imageUrl, trigger) {
      this.ensureMediaModal();

      this.mediaModalTrigger = trigger || null;
      this.mediaModalImage.src = imageUrl;
      this.mediaModal.hidden = false;
      this.mediaModal.setAttribute('aria-hidden', 'false');

      document.documentElement.classList.add('goodlot-loox-media-modal-open');
      document.addEventListener('keydown', this.mediaModalKeydownHandler);

      window.requestAnimationFrame(() => {
        this.mediaModal.classList.add('is-open');
        this.mediaModalClose?.focus({ preventScroll: true });
      });
    }

    closeMediaModal() {
      if (!this.mediaModal || this.mediaModal.hidden) return;

      this.mediaModal.classList.remove('is-open');
      this.mediaModal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('goodlot-loox-media-modal-open');
      document.removeEventListener('keydown', this.mediaModalKeydownHandler);

      const trigger = this.mediaModalTrigger;
      this.mediaModalTrigger = null;

      window.setTimeout(() => {
        if (!this.mediaModal) return;

        this.mediaModal.hidden = true;
        if (this.mediaModalImage) this.mediaModalImage.src = '';

        trigger?.focus?.({ preventScroll: true });
      }, 180);
    }

    reviewTemplate(review) {
      const media = review.media
        .map((item) => {
          const safeUrl = escapeHtml(item.url);
          const safeThumb = escapeHtml(item.thumbnail || item.url);

          if (item.type.includes('video')) {
            return `
              <a
                class="goodlot-loox-review__media-link"
                href="${safeUrl}"
                target="_blank"
                rel="noopener noreferrer"
              >
                <video src="${safeThumb}" muted playsinline preload="metadata"></video>
              </a>
            `;
          }

          return `
            <button
              class="goodlot-loox-review__media-link goodlot-loox-review__media-button"
              type="button"
              data-goodlot-review-photo-open="${safeUrl}"
              aria-label="Open review photo"
            >
              <img src="${safeThumb}" alt="" loading="lazy">
            </button>
          `;
        })
        .join('');

      const productPill =
        review.productTitle && review.productUrl
          ? `
              <a
                class="goodlot-loox-review__product"
                href="${escapeHtml(review.productUrl)}"
                aria-label="View ${escapeHtml(review.productTitle)}"
              >
                <span class="goodlot-loox-review__product-label">
                  ${escapeHtml(review.productTitle)}
                </span>
                <svg
                  class="goodlot-loox-review__product-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M5 12h14M13 6l6 6-6 6"></path>
                </svg>
              </a>
            `
          : review.productTitle
            ? `<span class="goodlot-loox-review__product">${escapeHtml(review.productTitle)}</span>`
            : '';

      const variantPill =
        review.variantTitle && review.variantTitle.toLowerCase() !== 'default title'
          ? `<span class="goodlot-loox-review__variant">${escapeHtml(review.variantTitle)}</span>`
          : '';

      return `
        <article class="goodlot-loox-review">
          <div class="goodlot-loox-review__top">
            <div class="goodlot-loox-review__person">
              <span class="goodlot-loox-review__name">${escapeHtml(review.reviewer)}</span>
              ${
                review.verified
                  ? '<span class="goodlot-loox-review__verified">Verified purchase</span>'
                  : ''
              }
            </div>
            ${
              review.date
                ? `<time class="goodlot-loox-review__date">${escapeHtml(formatDate(review.date))}</time>`
                : ''
            }
          </div>

          <div
            class="goodlot-loox-review__stars"
            role="img"
            aria-label="${review.rating.toFixed(1)} out of 5 stars"
          >
            ${renderStars(review.rating)}
          </div>

          ${
            review.title
              ? `<h3 class="goodlot-loox-review__title">${escapeHtml(review.title)}</h3>`
              : ''
          }

          ${
            review.body
              ? `<p class="goodlot-loox-review__body">${escapeHtml(review.body)}</p>`
              : ''
          }

          ${media ? `<div class="goodlot-loox-review__media">${media}</div>` : ''}

          ${
            productPill || variantPill || review.helpful > 0
              ? `
                <div class="goodlot-loox-review__bottom">
                  ${variantPill}
                  ${productPill}
                  ${
                    review.helpful > 0
                      ? `<span class="goodlot-loox-review__helpful">Helpful (${review.helpful})</span>`
                      : ''
                  }
                </div>
              `
              : ''
          }
        </article>
      `;
    }

    async appendReviewPage(url) {
      const result = await this.fetchAndMerge(url);
      this.nextPageUrl = result.next || '';
      return result.added;
    }

    async handleAllReviews() {
      if (!this.allReviewsButton || this.allReviewsButton.disabled) return;

      /*
       * The dataset, filter totals, and Customers mention are already based on
       * all public reviews. Clicking More reviews changes visibility only.
       */
      this.visibleCount = Math.min(
        this.visibleCount + this.revealStep,
        this.filteredReviews.length
      );

      this.applyFilter(this.activeFilter);
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.goodlotLooxInitialized === 'true') return;

      try {
        root.dataset.goodlotLooxInitialized = 'true';
        new GoodLotLooxReviews(root).init();
      } catch (error) {
        root.dataset.goodlotLooxInitialized = 'false';
        console.warn('[goodlot-loox-reviews] Initialization failed:', error);

        const status = root.querySelector('[data-goodlot-review-status]');
        const list = root.querySelector('[data-goodlot-review-list]');
        const allReviews = root.querySelector('[data-goodlot-review-all]');

        if (status) status.hidden = true;
        if (allReviews) allReviews.hidden = true;
        if (list) {
          list.innerHTML = `
            <p class="goodlot-loox-reviews__empty">
              Reviews are temporarily unavailable. Please refresh and try again.
            </p>
          `;
        }
      }
    });
  };

  const boot = () => init(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  window.setTimeout(boot, 0);

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();